import { browserActionVerificationReceiptSchema } from "./browserActionOutcome.ts";
import type { BrowserSavedTaskActionReq } from "./browserSavedTask.ts";
import { z } from "zod";
import { shoppingCardSpec, catalogOrderSummary } from "./shoppingCatalog.ts";
import type { ShoppingActionReq } from "./shopping.ts";
import { ROUTINE_DESCRIPTION_MAX_LENGTH, triggerConfigSchema } from "./routines.ts";
import { artifactView } from "./artifacts-view.ts";
import { reminderChannels } from "./calendarEvent.ts";
import { derivedThreadStatus } from "./threadCardStatus.ts";
import { paymentPilotSummarySchema } from "./paymentPilot.ts";
import {
  allDayRange,
  artifactSummary,
  draftAttachmentSchema,
  emailDraftParticipant,
  eventParticipant,
  formatReminderOverride,
  headerIcon,
  reminderOverrideSchema,
  schedulingKickoffParticipant,
  virtualMeeting,
  virtualMeetingType,
  type AllDayRange,
  type ArtifactSummary,
  type ArtifactSummaryExcerpt,
  type ArtifactSummaryField,
  connectorIconVariant,
  type DraftAttachment,
  type EmailDraftParticipant,
  type EventParticipant,
  type HeaderIcon,
  type ReminderOverride,
  type SchedulingKickoffParticipant,
  type VirtualMeeting,
} from "./artifacts-shared.ts";

// Re-export shared schemas so existing imports from `./artifacts.ts` keep working.
export {
  allDayRange,
  artifactSummary,
  draftAttachmentSchema,
  emailDraftParticipant,
  eventParticipant,
  formatReminderOverride,
  headerIcon,
  reminderOverrideSchema,
  schedulingKickoffParticipant,
  virtualMeeting,
  virtualMeetingType,
};
export type {
  AllDayRange,
  ArtifactSummary,
  ArtifactSummaryExcerpt,
  ArtifactSummaryField,
  DraftAttachment,
  EmailDraftParticipant,
  EventParticipant,
  HeaderIcon,
  ReminderOverride,
  SchedulingKickoffParticipant,
  VirtualMeeting,
};

/** External channel through which a message was sent/received. */
export const externalChannel = z.enum(["textMessage", "slack", "teams", "whatsapp"]);
export type ExternalChannel = z.infer<typeof externalChannel>;

const baseArtifact = z.object({
  id: z.string(),
  previousVersionId: z.string().optional(),
  version: z.number(),
  messageId: z.number(),
  threadId: z.number().nullable().optional(),
  artifactType: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isHeavy: z.literal(false),
  urn: z.string().optional(),
  /**
   * Render-time trust signal, not persisted. Unset / false = treat as
   * adversarial (the safe default); the retriever in `context/artifacts.ts`
   * sets it to true only when it can prove the bytes came from the user
   * themselves (e.g. a `communicationReceived` whose `from` is one of the
   * account's routable addresses). `renderArtifactEnvelope` reads this to
   * decide whether to emit `untrusted="true"` on the artifact envelope.
   * Recomputed every hydration — change the rule without a migration.
   */
  isTrusted: z.boolean().optional(),
  /**
   * Per-request access facts, not persisted — recomputed on every read, exactly
   * like {@link baseArtifact.isTrusted}. Absent on the many internal callers
   * that never render.
   *
   * `isOwner` is false for someone reading through a share grant; `canEdit` is
   * what the client keys read-only rendering off. They are separate because a
   * grant will eventually carry a write role, at which point a non-owner can
   * edit — collapsing them now would have to be undone then.
   */
  isOwner: z.boolean().optional(),
  canEdit: z.boolean().optional(),
});

/**
 * Declarative action metadata attached to an artifact response. The client
 * renders these as buttons (single-pill / split-pill) and routes clicks to
 * `handlerPath`. The array is ordered — index 0 is the primary action,
 * subsequent entries are secondary. Empty/undefined arrays mean "no actions
 * available".
 */
export const artifactActionVariant = z.enum(["primary", "secondary", "tertiary", "destructive"]);
export type ArtifactActionVariant = z.infer<typeof artifactActionVariant>;

export const artifactActionMethod = z.enum(["POST", "GET_EXTERNAL"]);
export type ArtifactActionMethod = z.infer<typeof artifactActionMethod>;

/**
 * Union of the existing per-handler Req schemas used as action bodies. Shared
 * between server (authoring `actions[]`) and client (consuming them), so the
 * same contracts each handler validates at receipt give you completions and
 * type-narrowing on both sides of the symlinked `types/` directory.
 *
 * TypeScript types are hoisted, so we can reference these Req aliases here
 * even though their zod schemas are defined further down in this file.
 */
export type ArtifactActionBody =
  | BrowserSavedTaskActionReq
  | SendDraftEmailReq
  | SendInvitationReq
  | MakePhoneCallReq
  | SendTextMessageReq
  | SendDirectMessageReq
  | SchedulingSuggestionActionReq
  | EventChangeActionReq
  | EmailChangeActionReq
  | ContactChangeActionReq
  | RoutineArtifactActionReq
  | OAuthConnectionActionReq
  | PickerActionReq
  | SchedulingKickoffActionReq
  | PendingApprovalActionReq
  | ShoppingActionReq
  | SubmitFormReq;

/**
 * Public TS shape of a single action. We intentionally define the TS type
 * first and then declare the zod schema via `z.custom<ArtifactAction>()` so
 * the narrowed `body` type propagates into every artifact response's
 * `actions?: ArtifactAction[]` field. Each handler re-validates `body` with
 * its own strict schema on receipt, so runtime validation here stays light.
 */
export type ArtifactAction = {
  requiresShoppingPayments?: boolean;
  /** Stable id (e.g. "send", "cancel", "accept") — used for analytics + react keys. */
  id: string;
  /** Short button label shown on the compact pill (e.g. "Send"). */
  label: string;
  /**
   * Optional expanded label used when the action is rendered in the overflow
   * dropdown — where horizontal space is cheap and descriptive copy helps
   * disambiguate secondary options (e.g. "Send this email to Alex" vs.
   * "Discard draft"). Falls back to `label` when omitted.
   */
  verboseLabel?: string;
  variant: ArtifactActionVariant;
  /** POST submits `body`; GET_EXTERNAL opens a page, using `externalMethod` when advertised. */
  method: ArtifactActionMethod;
  /** GET_EXTERNAL may POST first and open the returned URL. Older clients ignore this hint. */
  externalMethod?: "POST";
  /**
   * The action can be repeated without consuming the artifact decision, so
   * clients keep it live and leave the thread's state alone when it fires.
   * Set it on any action whose work happens outside this thread.
   */
  reusable?: boolean;
  /** Absolute or relative URL. For POST, the API route; for GET_EXTERNAL, an external URL. */
  handlerPath: string;
  /** Default request body for POST methods. Narrowed to one of the handler Req types. */
  body?: ArtifactActionBody;
  /** Optional `data-statsig-name` value for analytics tracking. */
  statsigName?: string;
  /**
   * Optional short status line for the action in flight — e.g. "Sending email…".
   *
   * @deprecated No client renders this; `pendingBody` carries the in-progress
   * copy on every surface. Do not author new values.
   */
  pendingToast?: string;
  /**
   * Optional card-body override applied as part of the optimistic in-progress
   * update — e.g. "Sending email to Frank, Kiran and 2 others." Displayed
   * instantly so the card reflects what the user just did; gets replaced
   * when the next server refresh (or socket update) arrives.
   */
  pendingBody?: string;
};

/**
 * Schema brand for `actions?: ArtifactAction[]` on every `heavyArtifact`
 * subtype. `z.custom<T>()` is a type-only brand — it carries the narrowed TS
 * shape (including the `ArtifactActionBody` union on `body`) through
 * `z.infer` without structural runtime validation. That's deliberate: the
 * server is the only producer (type-checked by `buildArtifactActions`) and
 * each handler re-validates its own request body on receipt with a strict
 * Req schema, so anything we'd check here would be duplicate work.
 */
export const artifactActionsSchema = z.custom<ArtifactAction[]>().optional();

const heavyArtifact = baseArtifact.extend({
  isHeavy: z.literal(true),
  artifactTypeTitle: z.string(),
  statusLabel: z.string(),
  status: z.string().default("completed"),
  actions: artifactActionsSchema,
  view: artifactView.optional(),
  /** Optional during rollout — server stamps this onto every heavy artifact in
   * `mapArtifact`. The client uses it as a presentation hint for the chrome
   * icon. Once every code path that produces a heavy artifact response goes
   * through `mapArtifact`, this can be made required. */
  iconName: headerIcon.optional(),
  /**
   * Title for the expanded chrome — lightbox, canvas card, native detail
   * screen, the native header the webview bridges a title to. Sibling of
   * `artifactTypeTitle` rather than a replacement: that one is a category
   * label, rendered in a single truncating line on the in-thread chip and
   * reused as Library-row and SMS link text, so it has to stay a short noun.
   * An expanded header has a full line to spend, and can say what this
   * particular card is asking.
   *
   * Absent for every type that has nothing longer to say, which is most of
   * them — surfaces fall back to `artifactDisplayTitle`.
   */
  expandedTitle: z.string().optional(),
  /** Compact biscuit summary, present only when this artifact is awaiting
   * user action and worth surfacing as an omnibox-overlay call-to-action.
   * Named `biscuit` (not `summary`) because some artifact types — notably
   * invitation — already have a `summary` field (the meeting title) that
   * would collide on the discriminated union. */
  biscuit: artifactSummary.optional(),
});

// Email
export const emailDraftArtifact = heavyArtifact.extend({
  artifactType: z.literal("emailDraft"),
  bcc: emailDraftParticipant.array().optional(),
  body: z.string(),
  cc: emailDraftParticipant.array().optional(),
  from: emailDraftParticipant,
  id: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date().optional(),
  // Client-minted correlation key, stable across a draft's versions. Set at
  // NEW_EMAIL/REPLY_EMAIL time, before the draft is ever persisted, so an
  // in-memory snapshot that was saved but never recaptured its saved id can
  // still be tied back to the row it created — SAVE/SEND resolve the existing
  // draft through it instead of minting a duplicate (BIG-6784).
  draftKey: z.string().optional(),
  subject: z.string(),
  to: emailDraftParticipant.array(),
  emailDraftType: z.enum(["reply", "replyAll", "forward", "new"]),
  /**
   * Who WROTE this draft, as distinct from whose address it leaves on.
   *
   * The two come apart when an account has no `email:send`: the user's own
   * message is forced onto okthx's address, so the from-contact says
   * "assistant" for a message the user wrote. Every signal at send time reads
   * the address, so authorship has to be recorded here at draft time or it is
   * gone.
   *
   * NOT what names the sender. Mail on okthx's address goes out as
   * "Okthx (Rino Jose)" whoever wrote it, because the name has to match the
   * address a reply would go to. This records a fact about the draft; it does
   * not decide what the recipient sees.
   *
   * Optional because drafts written before this field existed have no answer.
   */
  authoredBy: z.enum(["user", "assistant"]).optional(),
  emailPurpose: z.enum([
    "standard",
    "meetingProposal",
    "responderMeetingProposal",
    "meetingConfirmation",
    "responderMeetingConfirmation",
    "kickoff",
    "convince",
    "autoReplyAddAssistant",
    "brief",
    "holdoutOutreach",
    "schedulingHandoff",
  ]),
  sourceToRespondOrForward: z
    .object({
      id: z.string(),
      title: z.string(),
    })
    .optional(),
  status: z
    .enum(["draft", "waitingForUserAction", "inProgress", "sent", "cancelled", "updated"])
    .default("draft"),
  associatedGeneratedContextIds: z.array(z.string()).optional(),
  // Files to attach to the outbound email. Optional during rollout and for the
  // common no-attachment draft. See `draftAttachmentSchema`.
  attachments: z.array(draftAttachmentSchema).optional(),
  externalSentMessageId: z.string().optional(),
  // RFC 2822 Message-ID assigned by the provider when this draft was sent. Used
  // to recognize the provider's echo of our own send (inbound
  // communicationReceived.incomingEmailMessageId === this value).
  externalSentEmailMessageId: z.string().optional(),
});
export type EmailDraftArtifact = z.infer<typeof emailDraftArtifact>;

/**
 * Free/busy status. Mapped to Google's `transparency` (transparent/opaque) and
 * Microsoft's `showAs` at the provider boundary.
 */
export const availability = z.enum(["free", "busy"]);
export type Availability = z.infer<typeof availability>;

/**
 * Who can see the event's details on a calendar the user shares. Mapped to
 * Google's `visibility` and Microsoft's `sensitivity` at the provider boundary,
 * where "default" means "inherit the calendar's own setting" and is written by
 * omitting the field.
 */
export const eventVisibility = z.enum(["default", "public", "private", "confidential"]);
export type EventVisibility = z.infer<typeof eventVisibility>;

export const baseEventArtifact = heavyArtifact.extend({
  summary: z.string(),
  description: z.string().optional(),
  participants: eventParticipant.array(),
  location: z.string().optional(),
  recurrenceDescription: z.string().optional(),
  rrule: z.array(z.string()).optional(),
  startDateMs: z.number().optional(),
  endDateMs: z.number().optional(),
  /**
   * ISO 8601 string for `startDateMs` in the user's timezone, including
   * UTC offset and IANA name (e.g.
   * "2025-05-20T14:00:00-07:00[America/Los_Angeles]"). Populated by
   * mappers that have access to the user's timezone — agents read this
   * instead of converting milliseconds themselves.
   */
  startDateISO: z.string().optional(),
  endDateISO: z.string().optional(),
  isAllDay: z.boolean().optional(),
  /**
   * How the event occupies the calendar. Absent means busy — the provider
   * default — so an event that never sets this behaves exactly as before.
   */
  availability: availability.optional(),
  /**
   * Who can see the event's details. Absent means the calendar's own default —
   * the provider default — so an event that never sets this behaves as before.
   */
  visibility: eventVisibility.optional(),
  /** Calendar this event belongs to (e.g. "phegaro@gmail.com") */
  calendarName: z.string().optional(),
  colorId: z.string().optional(),
  reminders: z.array(reminderOverrideSchema).optional(),
});
export const calendarProvider = z.enum(["google", "microsoft"]);

export const invitationArtifact = baseEventArtifact.extend({
  artifactType: z.literal("invitation"),
  status: z
    .enum(["draft", "waitingForUserAction", "inProgress", "sent", "cancelled", "failed", "updated"])
    .default("draft"),
  htmlLink: z.string().optional(),
  virtualMeetingType: virtualMeetingType.optional(),
  virtualRoomUrl: z.string().optional(),
  provider: calendarProvider.optional(),
  calendarId: z.string().optional(),
  // The user asked to be left off this event. Carried through the mapped
  // artifact so the choice survives the agent re-loading the draft in a later
  // turn — see ent/schema/artifacts.ts for who sets it.
  excludeSelf: z.boolean().optional(),
  // True when this invitation is a travel-buffer hold bracketing a meeting,
  // rather than the meeting itself. The scheduling organizer relies on this to
  // avoid adopting a buffer as the tracked meeting slot (BIG-5390).
  isTravelBuffer: z.boolean().optional(),
});
export type InvitationArtifact = z.infer<typeof invitationArtifact>;

export const documentArtifact = heavyArtifact.extend({
  content: z.string(),
  title: z.string().nullable(),
  artifactType: z.literal("document"),
  /**
   * Whether this ent is the current head of its lineage — i.e. whether a share
   * link would serve exactly this row.
   *
   * Only the head may be shared: a link is anchored to the lineage, so sharing
   * from a prior version would hand out the *newest* content while the user was
   * looking at an older one. Optional because it is stamped by the artifact-list
   * path; consumers must treat absent as "not the head" so a client running
   * ahead of the server hides the control rather than offering a misleading one.
   */
  isLatestVersion: z.boolean().optional(),
});
export type DocumentArtifact = z.infer<typeof documentArtifact>;

// Applet — a self-contained single-page React app. `content` is the app source
// (the React component body); the client wraps it in the code-owned shell to
// render it in a sandboxed iframe. Mirrors documentArtifact's lifecycle.
export const appletArtifact = heavyArtifact.extend({
  content: z.string(),
  title: z.string().nullable(),
  artifactType: z.literal("applet"),
  /** See {@link documentArtifact}. */
  isLatestVersion: z.boolean().optional(),
});
export type AppletArtifact = z.infer<typeof appletArtifact>;

export const artifactSource = z.object({
  id: z.number(),
  type: z.enum(["email", "article", "update", "document", "memory", "message", "artifact"]),
  title: z.string(),
  summary: z.string().optional(),
  date: z.coerce.date().optional(),
});

/**
 * Optional structured payload an option may carry across the form roundtrip.
 * `contextData` persists with the userResponse artifact, so server-side
 * consumers can read it after the user picks the option without rerunning
 * any expensive resolution that produced it.
 *
 * Used by BIG-4267: distance-confirmation forms attach the resolved
 * location data here so `setLocation` / `setResolvedLocation` /
 * `generateContent` can short-circuit re-resolution on user confirmation.
 */
export const askUserFormFieldOptionSchema = z.object({
  id: z.string().optional(),
  label: z.string(),
  value: z.string(),
  contextData: z.unknown().optional(),
});
export type AskUserFormFieldOption = z.infer<typeof askUserFormFieldOptionSchema>;

export const askUserFormFieldSelectOptionSchema = z.object({
  type: z.literal("select"),
  id: z.string(),
  question: z.string(),
  options: z.array(askUserFormFieldOptionSchema),
});

export const askUserFormFieldSelectHandleSchema = askUserFormFieldSelectOptionSchema.extend({
  type: z.literal("selectHandle"),
  handleType: z.enum(["emailAddress", "phoneNumber"]),
  /**
   * Contact context so a user-typed value that matches none of the offered
   * options can still be saved as a real handle (so the agent resolves it next
   * run). `contactId` is set when the form disambiguates a single contact's
   * multiple handles — the new value is added to that contact. `contactName` is
   * set when the form disambiguates multiple distinct contacts that matched a
   * searched name — a typed-new value creates a contact with that name. Both
   * optional: forms created before these fields existed (or generated without a
   * name in scope) simply accept the typed value without persisting it.
   */
  contactId: z.string().optional(),
  contactName: z.string().optional(),
});

export type AskUserFormFieldSelectOption = z.infer<typeof askUserFormFieldSelectOptionSchema>;
export type AskUserFormFieldSelectHandle = z.infer<typeof askUserFormFieldSelectHandleSchema>;

export const askUserFormFieldNewContactSchema = z.object({
  type: z.literal("newContact"),
  id: z.string(),
  question: z.string(),
  contactName: z.string(),
  handleType: z.enum(["emailAddress", "phoneNumber"]),
});

export type AskUserFormFieldNewContact = z.infer<typeof askUserFormFieldNewContactSchema>;

export const askUserFormFieldNewHandleSchema = z.object({
  type: z.literal("newHandle"),
  id: z.string(),
  question: z.string(),
  handleValue: z.string(),
  contactId: z.string(),
  handleType: z.enum(["emailAddress", "phoneNumber"]),
});

export type AskUserFormFieldNewHandle = z.infer<typeof askUserFormFieldNewHandleSchema>;

export const askUserFormFieldTextSchema = z.object({
  type: z.literal("text"),
  id: z.string(),
  question: z.string(),
  inputType: z.enum(["text", "number", "date"]).optional(),
});

export type AskUserFormFieldText = z.infer<typeof askUserFormFieldTextSchema>;

/**
 * "Pick any number of these" — the list counterpart to `select`.
 *
 * Deliberately unbounded: no minimum, no maximum. A count constraint would be a
 * limit on the user's answer, and the surfaces that render this all offer a way
 * around one anyway, so enforcing it would be theatre.
 */
export const askUserFormFieldMultiSelectSchema = z.object({
  type: z.literal("multiSelect"),
  id: z.string(),
  question: z.string(),
  options: z.array(askUserFormFieldOptionSchema),
});

export type AskUserFormFieldMultiSelect = z.infer<typeof askUserFormFieldMultiSelectSchema>;

export const askUserFormFieldSchema = z.discriminatedUnion("type", [
  askUserFormFieldSelectOptionSchema,
  askUserFormFieldSelectHandleSchema,
  askUserFormFieldNewContactSchema,
  askUserFormFieldNewHandleSchema,
  askUserFormFieldTextSchema,
  askUserFormFieldMultiSelectSchema,
]);

export type AskUserFormField = z.infer<typeof askUserFormFieldSchema>;

export const askUserFormSchema = z.object({
  fields: z.array(askUserFormFieldSchema),
  values: z.record(z.string(), z.string()).optional(),
  /**
   * Answered some other way than by submitting — see {@link formAwaitsAnswer}.
   * Optional, so a client that predates it keeps parsing.
   */
  supersededAt: z.string().optional(),
});

export type AskUserFormSchema = z.infer<typeof askUserFormSchema>;

/**
 * A `multiSelect` answer is stored in `values` as a JSON array of the chosen
 * option values. `values` is one flat `Record<string, string>` shared by every
 * field type, so encoding the list keeps every existing consumer — quick
 * actions, the SMS renderer, the per-field side effects — compiling and
 * behaving exactly as before, none of which handle multi-select fields. The
 * alternative, widening the record to `string | string[]`, would spread a
 * branch across call sites that can never receive an array.
 *
 * The tradeoff is a value whose type understates it, so treat these two
 * functions as the only way in and out. Never `JSON.parse` a form value inline.
 */
export const encodeMultiSelectValue = (selected: readonly string[]): string =>
  JSON.stringify(selected);

export const decodeMultiSelectValue = (raw: string | undefined): string[] => {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((value): value is string => typeof value === "string");
  } catch {
    // Not JSON — a value submitted before this field type existed, or written
    // by hand. Read it as a single selection rather than dropping the answer.
    return [raw];
  }
};

/**
 * A message the agent is sending to the user — the reply text plus an
 * optional structured form for the user to fill in. Pairs with
 * {@link userRequestArtifact}.
 *
 * Naming gotcha: "user" names the user-side party in the exchange, not the
 * author.
 */
export const userResponseArtifact = baseArtifact.extend({
  artifactType: z.literal("userResponse"),
  response: z.string(),
  /**
   * Streaming lifecycle. `inProgress` while the reply streams (the live reveal
   * buffer is authoritative and `response` is still empty); `completed` once the
   * authoritative `response` is filled; `aborted` if the attempt was discarded
   * (the row is then hidden). Optional/absent for non-streamed replies, which
   * just render `response`.
   */
  status: z.enum(["inProgress", "completed", "aborted"]).optional(),
  form: askUserFormSchema.optional(),
  /** Set when the response was delivered via an external channel (e.g. text message) */
  channel: externalChannel.optional(),
  /**
   * Marks a reply that is an emoji reaction to `targetMessageId` rather than
   * prose. `response` holds the emoji. Clients that know the marker render it as
   * a badge on the target message instead of a bubble.
   */
  kind: z.literal("reaction").optional(),
  /** The user message a `reaction` is attached to. */
  targetMessageId: z.number().optional(),
  /**
   * Server-emitted quick-action buttons for simple single-select forms so the
   * user can answer directly from the home card / thread view without
   * opening the full `AskUserForm` UI. Populated only when the artifact has
   * exactly one `select` / `selectHandle` field and no `values` yet; each
   * option becomes one entry whose POST body submits that option's value.
   */
  actions: artifactActionsSchema,
});
export type UserResponseArtifact = z.infer<typeof userResponseArtifact>;

// Phone call participant (similar to email participant)
export const phoneCallParticipant = z.object({
  id: z.string(),
  urn: z.string(),
  name: z.string(),
  picture: z.string().optional(),
  value: z.string(), // phone number
  type: z.literal("phoneNumber"),
});
export type PhoneCallParticipant = z.infer<typeof phoneCallParticipant>;

// Text message participant (same as phone call participant)
export const textMessageParticipant = z.object({
  id: z.string(),
  urn: z.string(),
  name: z.string(),
  picture: z.string().optional(),
  value: z.string(), // phone number
  type: z.literal("phoneNumber"),
  contactId: z.string(),
});
export type TextMessageParticipant = z.infer<typeof textMessageParticipant>;

// Phone call draft artifact
export const phoneCallDraftArtifact = heavyArtifact.extend({
  artifactType: z.literal("phoneCallDraft"),
  recipient: phoneCallParticipant.optional(), // Mapped participant object (like email's 'from', 'to' fields)
  recipientName: z.string().optional(), // Display name for the recipient (e.g., business name)
  customInstructions: z.string().optional(),
  availableTimes: z.string().optional(),
  busyTimes: z.string().optional(),
  meetingDetails: z.string().optional(),
  phoneCallType: z.enum(["timeCoordination", "appointment", "reservation", "custom"]).optional(),
  vapiCallId: z.string().optional(),
  status: z
    .enum([
      "draft",
      "waitingForUserAction",
      "ringing",
      "queued",
      "inConversation",
      "inProgress",
      "completed",
      "cancelled",
      "failed",
      "updated",
    ])
    .default("draft"),
});
export type PhoneCallDraftArtifact = z.infer<typeof phoneCallDraftArtifact>;

// Text message draft artifact
export const textMessageDraftArtifact = heavyArtifact.extend({
  artifactType: z.literal("textMessageDraft"),
  from: textMessageParticipant,
  to: z.array(textMessageParticipant), // Supports broadcast to multiple recipients
  body: z.string(),
  /**
   * What sending this did. Absent: `body` went out as a text. `"reaction"`:
   * `body` is the emoji put on the user's own message as a tapback, and the
   * draft is the hidden record of what the channel currently shows there, so
   * a swap knows which reaction to take off first.
   */
  kind: z.enum(["message", "reaction"]).optional(),
  status: z
    .enum(["draft", "waitingForUserAction", "inProgress", "sent", "cancelled", "failed", "updated"])
    .default("draft"),
  /**
   * Provider delivery truth for a sent draft. `status: "sent"` means SendBlue
   * accepted the send; this says whether it actually arrived. A terminally
   * failed delivery also flips `status` to "failed" (keeping this "failed"),
   * so a draft with both is a real send that never arrived — distinct from a
   * send the API refused, which has `status: "failed"` and no deliveryState.
   * Absent on drafts predating delivery tracking and on simulated sends —
   * treat absent as the legacy final "Sent" state.
   */
  deliveryState: z.enum(["pending", "delivered", "failed"]).optional(),
  /** The note for the group's agent that goes with this text. Not shown on the card. */
  handoffNote: z.string().optional(),
  /** GCS file ids of the images this text carries. One rides the send. */
  mediaFileIds: z.array(z.string()).optional(),
  sourceToRespond: z
    .object({
      id: z.string(),
      title: z.string(),
    })
    .optional(),
});
export type TextMessageDraftArtifact = z.infer<typeof textMessageDraftArtifact>;

/** Largest value a Postgres bigint holds; a larger id throws building the query. */
const MAX_BIGINT = 9223372036854775807n;

/** Which text message draft's image `GET /artifacts/:artifactId/textMessageImage` serves. */
export const textMessageImageParam = z.object({
  artifactId: z.string().refine((id) => /^\d{1,19}$/.test(id) && BigInt(id) <= MAX_BIGINT),
});

// Direct message participant (display-only, no internal IDs sent to client)
export const directMessageParticipant = z.object({
  id: z.string(),
  name: z.string(),
  value: z.string(),
  picture: z.string().optional(),
});
export type DirectMessageParticipant = z.infer<typeof directMessageParticipant>;

// Direct message draft artifact
export const directMessageDraftArtifact = heavyArtifact.extend({
  artifactType: z.literal("directMessageDraft"),
  fromUser: directMessageParticipant,
  toUser: directMessageParticipant,
  body: z.string(),
  status: z
    .enum(["draft", "waitingForUserAction", "inProgress", "sent", "cancelled", "updated"])
    .default("draft"),
});
export type DirectMessageDraftArtifact = z.infer<typeof directMessageDraftArtifact>;

/**
 * Outbound message awaiting approval on a connector-backed channel (Slack,
 * Teams). The card renders the destination + body; `target` stays server-side
 * because it is per-connector addressing the user has no use for.
 */
export const externalMessageDraftArtifact = heavyArtifact.extend({
  artifactType: z.literal("externalMessageDraft"),
  /** Connector type / routing key — "slack", "teams", … */
  channel: z.string(),
  /**
   * What approving this does. Absent → send `body` as a message. `"reaction"`
   * → apply `body` (an emoji shortcode) to a message, which the card renders
   * as a reaction rather than as editable prose.
   */
  kind: z.enum(["message", "reaction"]).optional(),
  /** Human label for the destination (e.g. "#eng", "Alex Chen"). */
  label: z.string().optional(),
  /**
   * Human label for the sending identity, when the connector has a better name
   * for it than "you" (e.g. "your Slack account (Acme Corp)", "your WhatsApp
   * (+1 415 555 0123)"). Only set for a send under the user's own identity.
   */
  fromLabel: z.string().optional(),
  /**
   * Who the message goes out as. The card's "From" row reads "You" or "Okthx"
   * from this, on every draft rather than only when `fromLabel` is set:
   * showing the row for one sender and omitting it for the other is what made
   * an Okthx send look unattributed.
   */
  authorship: z.enum(["assistant", "user"]).optional(),
  /** A caution to surface on the approval card (e.g. cold first-contact). */
  warning: z.string().optional(),
  body: z.string(),
  /**
   * Slack display names for the `<@U…>` mentions in `body`, by Slack user id.
   * The view reads it to render the body for a person; `body` itself stays the
   * wire form, because that is what gets sent.
   */
  mentions: z.record(z.string(), z.string()).optional(),
  /** Files shared after the message, from the same sender. */
  attachments: z.array(draftAttachmentSchema).optional(),
  status: z
    .enum([
      "draft",
      "waitingForUserAction",
      "inProgress",
      "sent",
      "cancelled",
      "failed",
      "skipped",
      "updated",
    ])
    .default("draft"),
});
export type ExternalMessageDraftArtifact = z.infer<typeof externalMessageDraftArtifact>;

/**
 * Shared shape for the two confirm-gated dynamic tool calls. `args` is echoed
 * back so the user can see exactly what the assistant is about to send —
 * that's the whole point of the approval gate.
 */
const toolCallArtifactFields = {
  /** Display name of the tool as the catalog exposes it. */
  toolName: z.string(),
  /** The Forthic word the agent invoked (e.g. "linear.CREATE_ISSUE"). */
  wordName: z.string(),
  args: z.record(z.string(), z.unknown()).default({}),
  status: z
    .enum(["waitingForUserAction", "inProgress", "completed", "cancelled", "failed", "updated"])
    .default("waitingForUserAction"),
  error: z.string().optional(),
};

/** Confirm-gated MCP tool call. */
export const mcpToolCallArtifact = heavyArtifact.extend({
  artifactType: z.literal("mcpToolCall"),
  /** Slug of the MCP server the tool lives on. */
  serverSlug: z.string(),
  ...toolCallArtifactFields,
});
export type McpToolCallArtifact = z.infer<typeof mcpToolCallArtifact>;

/** Safe client projection of a confirm-gated Kernel browser action. */
export const browserActionArtifact = heavyArtifact.extend({
  artifactType: z.literal("browserAction"),
  savedBrowserTask: z
    .object({
      taskId: z.string(),
      revision: z.number(),
      suspended: z.boolean(),
      queued: z.boolean(),
    })
    .optional(),
  verification: browserActionVerificationReceiptSchema.optional(),
  site: z.string(),
  action: z.string(),
  consequence: z.string(),
  approvalFormArtifactId: z.string(),
  screenshotFrameId: z.string().optional(),
  status: z.enum([
    "waitingForUserAction",
    "inProgress",
    "completed",
    "cancelled",
    "failed",
    "outcomeUnknown",
    "updated",
  ]),
  error: z.string().optional(),
});
export type BrowserActionArtifact = z.infer<typeof browserActionArtifact>;

/** Confirm-gated dynamic external (OpenAPI / Google Discovery) tool call. */
export const externalToolCallArtifact = heavyArtifact.extend({
  artifactType: z.literal("externalToolCall"),
  sourceKind: z.enum(["openapi"]),
  /** Slug of the API the tool belongs to. */
  sourceSlug: z.string(),
  ...toolCallArtifactFields,
});
export type ExternalToolCallArtifact = z.infer<typeof externalToolCallArtifact>;

/** Confirm-gated Google Drive mutation (trash or share). */
export const gdriveActionArtifact = heavyArtifact.extend({
  artifactType: z.literal("gdriveAction"),
  action: z.enum(["trash", "share"]),
  fileName: z.string(),
  /** Drive web link for the file, so the card can deep-link out. */
  fileUrl: z.string().optional(),
  /** Share only — the grantee and the role they'd receive. */
  email: z.string().optional(),
  role: z.enum(["reader", "commenter", "writer"]).optional(),
  notify: z.boolean().optional(),
  status: z
    .enum(["waitingForUserAction", "inProgress", "completed", "cancelled", "failed", "updated"])
    .default("waitingForUserAction"),
  error: z.string().optional(),
});
export type GDriveActionArtifact = z.infer<typeof gdriveActionArtifact>;

// Call transcript artifact
export const callResultsArtifact = heavyArtifact.extend({
  artifactType: z.literal("callResults"),
  vapiCallId: z.string(),
  messages: z.array(
    z.object({
      role: z.string(),
      message: z.string(),
      time: z.number(),
      endTime: z.number().optional(),
      secondsFromStart: z.number(),
    }),
  ),
  duration: z.number(), // Duration in seconds
  /**
   * Origin-relative, session-authenticated path to play the recording — NOT the
   * provider's URL. The URL Vapi stores on the ent points at a host that does
   * not resolve; the bytes only reach a client through our proxy.
   */
  recordingUrl: z.string().optional(),
  participant: phoneCallParticipant,
  callStartTime: z.coerce.date(),
  callEndTime: z.coerce.date(),
  /** Vapi's reason the call ended — notably who hung up. See the ent schema. */
  endedReason: z.string().optional(),
  /** The participant called the okthx number back. See the ent schema. */
  inbound: z.boolean().optional(),
  analysis: z.object({
    summary: z.string().optional(),
    successEvaluation: z.string().optional(),
  }),
  phoneCallDraftId: z.string().optional(), // Link back to original phone call draft
});
export type CallResultsArtifact = z.infer<typeof callResultsArtifact>;

/**
 * A file stored with a received email: one the sender attached, or an image the
 * body shows inline through `cid:<contentId>`.
 */
export const receivedEmailFile = z.object({
  fileId: z.string(),
  fileName: z.string(),
  contentType: z.string(),
  size: z.number(),
  contentId: z.string().optional(),
});

// Email received artifact (external communication)
export const communicationReceivedArtifact = heavyArtifact.extend({
  artifactType: z.literal("communicationReceived"),
  from: emailDraftParticipant,
  to: emailDraftParticipant.array(),
  cc: emailDraftParticipant.array().optional(),
  bcc: emailDraftParticipant.array().optional(),
  subject: z.string(),
  body: z.string(), // Raw HTML body, or plain text when there is no HTML part
  plainTextBody: z.string().optional(),
  receivedAt: z.coerce.date(),
  sourceId: z.string(), // Reference to source entity
  // The conversation this message belongs to, as one id that holds across
  // providers — the same conversation seen through Gmail, Outlook and Postmark
  // resolves to one value. Lets the agent ground "this email thread" — pair with
  // `search.SEARCH thread:<id>` to fetch the rest of the chain.
  externalThreadId: z.string().optional(),
  // RFC 2822 Message-ID of the received copy. When this inbound is the
  // provider re-ingesting our own send, this equals the sending
  // emailDraft's externalSentEmailMessageId.
  incomingEmailMessageId: z.string().optional(),
  status: z
    .enum(["received", "waitingForUserAction", "inProgress", "approved", "cancelled", "updated"])
    .default("received"),
  suggestedActionText: z.string().optional(),
  // Read across the email's stored copies, so a thread anchored on a copy that
  // keeps no files still finds them. Feeds the card's inline images and its
  // attachment list.
  attachments: z.array(receivedEmailFile).optional(),
});
export type CommunicationReceivedArtifact = z.infer<typeof communicationReceivedArtifact>;

// Invitation response artifact for calendar responses to an invite we sent out
export const invitationResponseArtifact = heavyArtifact.extend({
  artifactType: z.literal("invitationResponse"),
  from: emailDraftParticipant,
  invitationId: z.string(),
  summary: z.string(),
  responseType: z.enum(["accepted", "declined", "tentative", "unknown"]),
  startDateMs: z.number().optional(),
  endDateMs: z.number().optional(),
  proposedStartDateMs: z.number().optional(),
  proposedEndDateMs: z.number().optional(),
  receivedAt: z.coerce.date(),
  invitationSourceId: z.string().optional(),
  communicationSourceId: z.string().optional(),
  eventSourceId: z.string().optional(),
  /** Calendar this event belongs to (e.g. "phegaro@gmail.com") */
  calendarName: z.string().optional(),
});
export type InvitationResponseArtifact = z.infer<typeof invitationResponseArtifact>;

// Invitation received artifact for new calendar invitations
/** A meeting's time before it moved: what an updated invitation carries as `movedFrom`. */
export const meetingWindowSchema = z.object({
  startDateMs: z.number(),
  endDateMs: z.number(),
  isAllDay: z.boolean().optional(),
});
export type MeetingWindow = z.infer<typeof meetingWindowSchema>;

/**
 * Where a moved meeting was, and the user's answer to that time when they had
 * given one. The answer is what makes a move worth asking about: the user
 * committed to the old time, and Google cleared it (BIG-7689).
 */
export const movedFromSchema = meetingWindowSchema.extend({
  previousResponse: z.enum(["accepted", "tentative", "declined"]).optional(),
});

export const invitationReceivedArtifact = baseEventArtifact.extend({
  artifactType: z.literal("invitationReceived"),
  externalEventId: z.string(),
  receivedAt: z.coerce.date(),
  virtualMeetings: virtualMeeting.array().optional(),
  /** The time before the organizer moved the meeting; unset when it never moved. */
  movedFrom: movedFromSchema.optional(),
  status: z
    .enum([
      "pending",
      "waitingForUserAction",
      "inProgress",
      "approved",
      "accepted",
      "tentative",
      "declined",
      "countered",
      "cancelled",
      "failed",
      "updated",
    ])
    .default("waitingForUserAction"),
  /**
   * True when the invite's time window collides with an existing calendar
   * event. Computed server-side when the artifact is at
   * `waitingForUserAction` so the card body can show a free/busy indicator
   * without a client-side round trip. Unset for non-waiting statuses.
   */
  hasConflict: z.boolean().optional(),
});
export type InvitationReceivedArtifact = z.infer<typeof invitationReceivedArtifact>;

// File artifact
export const fileArtifact = heavyArtifact.extend({
  artifactType: z.literal("file"),
  filename: z.string(),
  mimeType: z.string(),
  summary: z.string(),
  sizeBytes: z.number().optional(),
  fileId: z.string().optional(),
  sourceId: z.string().optional(),
  // Async-summarization outcome (see the `file` ent schema). Absent → treat as
  // "summarized". "failed" rows render as a failed chip and carry `errorMessage`.
  summaryStatus: z.enum(["summarized", "failed"]).optional(),
  errorMessage: z.string().optional(),
  /** See {@link documentArtifact}. */
  isLatestVersion: z.boolean().optional(),
});
export type FileArtifact = z.infer<typeof fileArtifact>;

// Event Change artifact - represents a proposed modification or RSVP to an existing event
// Multiple changes can be represented in a single artifact by setting multiple new* fields
export const eventChangeArtifact = heavyArtifact.extend({
  artifactType: z.literal("eventChange"),
  eventSourceUrn: z.string(),
  externalEventId: z.string().optional(),
  externalCalendarId: z.string().optional(),
  /** Calendar this event belongs to (e.g. "phegaro@gmail.com") */
  calendarName: z.string().optional(),
  provider: calendarProvider.optional(),
  sourceType: z.enum(["invitation", "invitationReceived", "eventSource"]),
  sourceId: z.string(),
  isOrganizer: z.boolean(),
  isWritable: z.boolean().default(true), // Whether user has write access to modify/delete this event
  /**
   * Whether this change reaches the organizer and other attendees, or only the
   * user's own calendar copy. Read by every surface that describes a
   * cancellation, so an attendee's self-removal is never announced as a
   * meeting-wide cancellation. `undefined` means "can reach everyone".
   */
  canModifyForEveryone: z.boolean().optional(),
  originalSummary: z.string().optional(),
  originalDescription: z.string().optional(),
  originalStartDateMs: z.number().optional(),
  originalEndDateMs: z.number().optional(),
  /**
   * ISO 8601 strings for the before/after start/end times in the user's
   * timezone (e.g. "2025-05-20T14:00:00-07:00[America/Los_Angeles]"),
   * populated by the mapper so agents/UI don't have to convert the `Ms`
   * fields themselves.
   */
  originalStartDateISO: z.string().optional(),
  originalEndDateISO: z.string().optional(),
  originalLocation: z.string().optional(),
  originalAvailability: availability.optional(),
  originalIsAllDay: z.boolean().optional(),
  isCancellation: z.boolean().default(false),
  responseType: z.enum(["accepted", "declined", "tentative"]).optional(),
  newStartDateMs: z.number().optional(),
  newEndDateMs: z.number().optional(),
  newStartDateISO: z.string().optional(),
  newEndDateISO: z.string().optional(),
  newSubject: z.string().optional(),
  newLocation: z.string().optional(),
  newDescription: z.string().optional(),
  newColorId: z.string().optional(),
  /** Change the event's free/busy state. Undefined = no change. */
  newAvailability: availability.optional(),
  /**
   * Convert the event to/from all-day. When set, newStartDateMs/newEndDateMs
   * carry the UTC-midnight range (start inclusive, end exclusive). Undefined =
   * no change.
   */
  newIsAllDay: z.boolean().optional(),
  /**
   * The all-day change's covered days as inclusive floating calendar dates —
   * the source of truth when the change is all-day. `newStartDateMs`/
   * `newEndDateMs` are its derived UTC-midnight projection, kept populated for
   * consumers of the epoch-ms shape. Absent on changes persisted before this
   * field existed and on timed changes (BIG-6782).
   */
  newAllDayRange: allDayRange.optional(),
  /** true = add a virtual meeting link (Google Meet / Teams / Zoom); false = remove it */
  addVirtualMeeting: z.boolean().optional(),
  newRecurrence: z.array(z.string()).nullish(),
  /** null/undefined = no change; [] = clear all reminders; [{...}, ...] = replace with this list */
  newReminders: z.array(reminderOverrideSchema).nullish(),
  editScope: z.enum(["thisEvent", "thisAndFollowing", "allEvents"]).optional(),
  attendeesToAdd: z.array(eventParticipant).optional(),
  attendeesToRemove: z.array(eventParticipant).optional(),
  status: z
    .enum([
      "draft",
      "waitingForUserAction",
      "inProgress",
      "completed",
      "cancelled",
      "failed",
      "updated",
    ])
    .default("draft"),
});
export type EventChangeArtifact = z.infer<typeof eventChangeArtifact>;

// Email Change artifact - represents a proposed bulk email management action
export const emailChangeEmailInfo = z.object({
  sourceUrn: z.string(),
  subject: z.string().optional(),
  senderName: z.string().optional(),
  emailUrl: z.string().optional(),
});

export const emailChangeArtifact = heavyArtifact.extend({
  artifactType: z.literal("emailChange"),
  operationType: z.enum([
    "markRead",
    "markUnread",
    "addLabels",
    "removeLabels",
    "archive",
    "star",
    "unstar",
    "trash",
    "markSpam",
    "markImportant",
  ]),
  /**
   * With `selector` present these are a sample for display, and `selector.threadCount`
   * is how many the operation actually covers. Without one they are the whole set.
   */
  emails: z.array(emailChangeEmailInfo),
  /**
   * Set when the change was proposed as a plan ("archive these four kinds of
   * mail") rather than as a selection. Deliberately carries only what a client
   * renders — the predicates stay server-side.
   */
  selector: z
    .object({
      /** Deduped union across every group — not the sum of the rows below. */
      threadCount: z.number(),
      /** What the inbox is left with once this is applied. */
      remainingThreads: z.number(),
      /** One line naming the plan. */
      description: z.string(),
      /** The rows: what each kind of mail is called and how much of it there is. */
      groups: z.array(z.object({ label: z.string(), threadCount: z.number() })),
    })
    .optional(),
  labelNames: z.array(z.string()).optional(),
  status: z
    .enum([
      "draft",
      "waitingForUserAction",
      "inProgress",
      "completed",
      "cancelled",
      "failed",
      "updated",
    ])
    .default("draft"),
});
export type EmailChangeArtifact = z.infer<typeof emailChangeArtifact>;

/**
 * How many emails an `emailChange` actually affects.
 *
 * `emails` means two different things depending on `selector` — the whole set
 * without one, a capped display sample with one — so every surface that reports
 * a number must resolve the overload the same way. It lives here, beside the
 * type that carries the ambiguity, because four separate surfaces read this
 * count (both artifact views, the Apply button copy, and the message written
 * into the thread on apply) and the first attempt at this feature updated two of
 * them. A helper is the only version of "audit every consumer" that survives the
 * next consumer being added.
 */
export function affectedEmailCount(change: {
  emails?: unknown[];
  selector?: { threadCount: number };
}): number {
  return change.selector?.threadCount ?? change.emails?.length ?? 0;
}

/**
 * Optimistic-write reconciliation id, spread into every action request whose
 * handler writes a user message.
 *
 * The client firing the action mints the id and the server stamps it onto the
 * message the action writes, so the `thread_event:update` frame carries an
 * `idToClientId` entry for it. That entry is what tells the feed the message is
 * this client's own send: it gets the active-message treatment (spacer + scroll
 * to the top, so the reply streams in below the fold) and skips the queued pill
 * stack, exactly as a typed message does.
 *
 * Optional everywhere. An action fired without one still applies — it just
 * doesn't move the feed — which is the right behavior for the callers that have
 * no feed to move (the native header bridge, the standalone artifact route).
 */
export const tempClientIdField = { tempClientId: z.number().optional() };

export const emailChangeActionReq = z.object({
  action: z.enum(["apply", "cancel"]),
  ...tempClientIdField,
});
export type EmailChangeActionReq = z.infer<typeof emailChangeActionReq>;

// Contact change artifact - an address or a preferred address that waits for
// the user's Approve before it is written to a person's card.
const contactChangeAddress = z.object({
  value: z.string(),
  type: z.enum(["emailAddress", "phoneNumber"]),
});

export const contactChangeArtifact = heavyArtifact.extend({
  artifactType: z.literal("contactChange"),
  contactId: z.string(),
  contactName: z.string(),
  addresses: z.array(contactChangeAddress),
  preferred: z.array(contactChangeAddress),
  currentPreferred: z.array(contactChangeAddress),
  newName: z.string().optional(),
  sourceThreadTitle: z.string().optional(),
  status: z
    .enum(["waitingForUserAction", "inProgress", "completed", "cancelled", "failed"])
    .default("waitingForUserAction"),
});
export type ContactChangeArtifact = z.infer<typeof contactChangeArtifact>;

export const contactChangeActionReq = z.object({
  action: z.enum(["apply", "cancel"]),
  ...tempClientIdField,
});
export type ContactChangeActionReq = z.infer<typeof contactChangeActionReq>;

/**
 * A message the user is sending to the agent. Today only used for
 * external-channel intake (SMS etc.); in-app chat stores its content
 * directly on the `messages` row. Pairs with {@link userResponseArtifact}.
 *
 * TODO: revisit if we start supporting more complex channels, e.g. email.
 * In that case we should consider another artifact type that references
 * other artifacts. For now we keep it simple since we just have the
 * content field.
 */
export const userRequestArtifact = baseArtifact.extend({
  artifactType: z.literal("userRequest"),
  content: z.string(),
  channel: externalChannel.optional(),
  /**
   * The provider's id for the message this request was filed from (a SendBlue
   * message handle, a WhatsApp message id). It is what a channel needs to put
   * a native reaction on the user's own message. Absent for requests typed in
   * the app and for messages filed before the field existed.
   */
  externalMessageId: z.string().optional(),
});
export type UserRequestArtifact = z.infer<typeof userRequestArtifact>;

/**
 * Thread artifact response — a card linking to a thread the user should open in
 * the app (soft-link at the current thread, or a spawned sibling). Heavy so it
 * renders through the server-built declarative `view` (title + live activity),
 * the same way the thread-list row reads. The target's `title` / `activityState`
 * and the base `status` are resolved live at map time, since the target
 * auto-titles itself and its lifecycle keeps changing.
 */
export const threadArtifactResp = heavyArtifact.extend({
  artifactType: z.literal("threadArtifact"),
  // No numeric thread id here — it would collide with the library's own
  // `threadId` (the artifact's *containing* thread). The target is carried as a
  // URN; the view derives the `/threads/:id` path from it.
  threadUrn: z.string(),
  title: z.string(),
  activityState: z.string(),
  // Presentation status shared with the homepage card (label/icon/severity keyed
  // on this in `threadCardStatus`), so both surfaces render the same badge.
  derivedStatus: derivedThreadStatus,
  // Card summary — the same body the homepage card shows for this thread.
  summary: z.string().optional(),
});
export type ThreadArtifactResp = z.infer<typeof threadArtifactResp>;

// Routine artifact - display view of a routine ent within a thread
export const routineArtifactResp = heavyArtifact.extend({
  artifactType: z.literal("routineArtifact"),
  routineEntId: z.string(),
  sourceSystemRoutineId: z.string().optional(),
  name: z.string(),
  description: z.string().optional(),
  // Read-only when `sourceSystemRoutineId` is set — the user's changes to a
  // system routine live in `amendment`, applied on top of this every run.
  prompt: z.string(),
  amendment: z.string().optional(),
  triggerConfig: triggerConfigSchema,
  permissions: z.object({
    autoSendCommunication: z.boolean(),
    autoArchive: z.boolean(),
    defaultFrom: z.enum(["user", "assistant"]),
  }),
  enabled: z.boolean(),
  // Absolute instant (ISO 8601, UTC) after which the installed routine stops
  // firing and turns itself off. Absent means it runs until disabled.
  endsAt: z.string().datetime().optional(),
  // A user routine's area and roles. Strings, so an app build parses an area
  // or role added later.
  area: z.string().optional(),
  roles: z.array(z.string()).optional(),
  status: z
    .enum(["draft", "waitingForUserAction", "approved", "cancelled", "updated"])
    .default("draft"),
});
export type RoutineArtifactResp = z.infer<typeof routineArtifactResp>;

// Skill artifact - a drafted skill awaiting the user's review inside a thread.
//
// `baseBody` is filled in by the mapper for an override, read live off the disk
// SKILL.md rather than stored on the artifact — the card shows it read-only
// beside the editable amendment, the same split the skills editor uses. It is
// absent for a user-authored skill, which owns its `body` outright.
export const skillArtifactResp = heavyArtifact.extend({
  artifactType: z.literal("skillArtifact"),
  skillEntId: z.string().optional(),
  name: z.string(),
  description: z.string(),
  kind: z.enum(["override", "user"]),
  body: z.string(),
  amendment: z.string(),
  baseBody: z.string().optional(),
  enabled: z.boolean(),
  status: z
    .enum(["draft", "waitingForUserAction", "approved", "cancelled", "updated"])
    .default("draft"),
});
export type SkillArtifactResp = z.infer<typeof skillArtifactResp>;

// Event artifact - provides calendar event context in routine-triggered threads
export const eventArtifactResp = baseEventArtifact.extend({
  artifactType: z.literal("eventArtifact"),
  sourceEntId: z.string(),
});
export type EventArtifactResp = z.infer<typeof eventArtifactResp>;

// Reminder artifact - living card mirroring an okthx reminder's current state.
// `participants` are the resolved recipients; a self-reminder resolves to none,
// since the owner is carried by the source rather than as a contact handle.
// A reminder's card. Every field but `sourceId` is read off the reminder itself
// when the card is built, so the card always says what the reminder currently
// says. `status` follows from the clock: a reminder whose time has passed has
// happened. The exception is `updated`: each fire adds a newer card at the end
// of the thread, and the one it replaced reads `updated`.
export const reminderArtifact = heavyArtifact.extend({
  artifactType: z.literal("reminder"),
  summary: z.string(),
  description: z.string().optional(),
  /** When it fires. A reminder is a moment, so there is no end. */
  startDateMs: z.number(),
  remindBy: z.enum(reminderChannels),
  /** Who gets pinged. Empty for a reminder to the user themselves. */
  participants: eventParticipant.array(),
  /** The reminder this card speaks for. */
  sourceId: z.string(),
  /** Present when the reminder repeats. */
  externalRecurringEventId: z.string().optional(),
  /** The repeat in plain words, e.g. "every Tuesday". */
  repeatText: z.string().optional(),
  status: z.enum(["scheduled", "fired", "cancelled", "updated"]).default("scheduled"),
});
export type ReminderArtifact = z.infer<typeof reminderArtifact>;

// OAuth connection artifact - provider-agnostic "Connect X" prompt
export const oauthConnectionArtifact = heavyArtifact.extend({
  artifactType: z.literal("oauthConnection"),
  savedBrowserTask: z
    .object({
      taskId: z.string(),
      revision: z.number(),
      suspended: z.boolean(),
      queued: z.boolean(),
    })
    .optional(),
  provider: z.string(),
  providerDisplayName: z.string(),
  connectionReason: z.string(),
  connectUrl: z.string(),
  /** Favicon source for connectors with no hand-drawn brand mark (MCP, OpenAPI). */
  websiteUrl: z.string().optional(),
  /** Which product mark to draw. Absent on cards written before the field existed. */
  iconVariant: connectorIconVariant.optional(),
  /** Missing on legacy cards and means the normal connector flow. */
  purpose: z.enum(["connector", "browserAuth"]).optional(),
  /** Derived display state for an expired browser-auth card. */
  browserAuthState: z.enum(["expired", "restarting"]).optional(),
  automaticSignInAvailable: z.boolean().optional(),
  status: z
    .enum(["waitingForUserAction", "completing", "cancelling", "connected", "cancelled", "updated"])
    .default("waitingForUserAction"),
});
export type OAuthConnectionArtifact = z.infer<typeof oauthConnectionArtifact>;

/**
 * Per-kind framing for {@link pickerArtifact}. Mirrors `connectorsPickerTarget`
 * in `ent/schema/artifacts.ts`; the two must be edited together.
 */
const connectorsPickerTarget = z.object({
  kind: z.literal("connectors"),
  /** Restrict the card to these catalog ids; empty means the whole catalog. */
  scopeIds: z.array(z.string()).default([]),
  /** Catalog ids pinned above the rest, with a separator under them. */
  pinnedIds: z.array(z.string()).default([]),
  /** Catalog ids most-likely-first for the unpinned remainder. */
  preferredIds: z.array(z.string()).default([]),
});

/**
 * The calendars kind carries no framing at all: every calendar the account has
 * is offered, in the order the client's own list returns them. There is nothing
 * to scope or rank — the user reads their own calendar names faster than any
 * inference about which of them "look like real commitments", and a calendar
 * filtered out is one they never get the chance to mark busy.
 */
const calendarsPickerTarget = z.object({
  kind: z.literal("calendars"),
});

/**
 * Recurring meetings are protected by default — the scheduler will not offer
 * that time to anyone. This card is where the user says which ones could
 * actually move. Every series is offered, in the order the client's own query
 * returns them; deciding which "look movable" on the user's behalf hides
 * options they would have picked.
 */
const protectedMeetingsPickerTarget = z.object({
  kind: z.literal("protectedMeetings"),
});

/**
 * The locations kind carries no framing either: there are exactly two addresses
 * to offer, home and work, and both are always shown. Which of them the user
 * fills in is the answer the card is asking for, so narrowing to one beforehand
 * would be answering it for them.
 */
const locationsPickerTarget = z.object({
  kind: z.literal("locations"),
});

/**
 * Routines are already running before the user arrives; this card is where they
 * see the whole set and turn things on or off. Every routine is offered,
 * enabled or not — one filtered out is one the user never learns exists.
 */
const routinesPickerTarget = z.object({
  kind: z.literal("routines"),
});

/**
 * The channels someone is in, to add okthx to after connecting Slack from a
 * chat card. Mirrors `slackChannelsPickerTarget` in `ent/schema/artifacts.ts`.
 * Names the workspace, since an account can connect more than one.
 */
const slackChannelsPickerTarget = z.object({
  kind: z.literal("slackChannels"),
  teamId: z.string(),
});

/**
 * A card the user picks things on, discriminated by what they're picking.
 * Carries only the framing — the client reads live state itself, so a row acted
 * on inside the card updates in place instead of the card going stale.
 */
export const pickerArtifact = heavyArtifact.extend({
  artifactType: z.literal("picker"),
  prompt: z.string(),
  target: z.discriminatedUnion("kind", [
    connectorsPickerTarget,
    calendarsPickerTarget,
    protectedMeetingsPickerTarget,
    locationsPickerTarget,
    routinesPickerTarget,
    slackChannelsPickerTarget,
  ]),
  /** Row names in display order, for the collapsed preview. */
  previewNames: z.array(z.string()).default([]),
  status: z
    .enum(["waitingForUserAction", "completed", "cancelled", "updated"])
    .default("waitingForUserAction"),
});
export type PickerArtifact = z.infer<typeof pickerArtifact>;

/** Every kind finishes the same way: the user says they're done picking. */
export const pickerActionReq = z.object({
  action: z.enum(["done"]),
  ...tempClientIdField,
});
export type PickerActionReq = z.infer<typeof pickerActionReq>;

export const oauthConnectionActionReq = z.object({
  action: z.enum(["cancel", "retry"]),
  ...tempClientIdField,
});
export type OAuthConnectionActionReq = z.infer<typeof oauthConnectionActionReq>;

/**
 * A request for the user to give the agent's computer an API key.
 *
 * Carries where the value will go and why it is wanted, and never the value —
 * it goes from the form to the provider's secret store without passing
 * through an artifact, so there is no field here for it to land in.
 */
export const computerSecretDestination = z.object({
  kind: z.literal("secretManager"),
  /** The variable the key is mounted as on the computer. */
  envVar: z.string(),
  /** The only host the real value is ever sent to. */
  host: z.string(),
  /**
   * The agent's note on what the key is for, stored with the key so a later
   * session can find it. Absent on cards raised before it existed.
   */
  purpose: z.string().optional(),
});
export type ComputerSecretDestination = z.infer<typeof computerSecretDestination>;

export const computerSecretArtifact = heavyArtifact.extend({
  artifactType: z.literal("computerSecret"),
  destination: computerSecretDestination,
  question: z.string(),
  status: z
    .enum(["waitingForUserAction", "saving", "saved", "cancelled"])
    .default("waitingForUserAction"),
});
export type ComputerSecretArtifact = z.infer<typeof computerSecretArtifact>;

/** Whether a string holds an ASCII control character, including CR, LF, tab and NUL. */
function hasControlCharacter(value: string): boolean {
  for (let index = 0; index < value.length; index++) {
    const code = value.charCodeAt(index);
    if (code < 0x20 || code === 0x7f) return true;
  }
  return false;
}

/** Submitting the value, or declining to. The two arrive at the same route. */
export const computerSecretActionReq = z.union([
  z.object({
    /**
     * Bounded before trimming, so a huge whitespace payload is refused rather
     * than trimmed down to something acceptable. Refused after trimming too:
     * `min(1)` alone accepts a single space, which would write an empty file
     * and leave the card reporting "Saved" over a credential that is not there.
     */
    value: z
      .string()
      .max(8192)
      .transform((v) => v.trim())
      .refine((v) => v.length > 0)
      // The provider places this value in an HTTP request header. A line break
      // or a NUL inside it is never part of a real key, and in a header it is
      // the raw material for splitting one request into two.
      .refine((v) => !hasControlCharacter(v)),
    ...tempClientIdField,
  }),
  z.object({ action: z.literal("cancel"), ...tempClientIdField }),
]);
export type ComputerSecretActionReq = z.infer<typeof computerSecretActionReq>;

/**
 * A standing offer to open the account's computer and use it directly.
 *
 * No URL, here or on the ent. The way in is minted per click and expires, so a
 * copy carried in the thread payload would be stale far sooner than the card it
 * rode in on.
 */
export const computerDesktopArtifact = heavyArtifact.extend({
  artifactType: z.literal("computerDesktop"),
  reason: z.string(),
  status: z.enum(["waitingForUserAction", "ready", "updated"]).default("waitingForUserAction"),
});
export type ComputerDesktopArtifact = z.infer<typeof computerDesktopArtifact>;

/**
 * What the mint route hands back: a link that works for a while, and nothing else.
 *
 * Constrained to https rather than left at `z.string().url()`, which accepts
 * `javascript:` and `data:`. Today the value comes from our own route, so this
 * is not reachable — but the clients treat it as a scheme check, and only one of
 * them has a CSP behind it: iOS hands the string to `Linking.openURL`, which
 * gives an arbitrary scheme to whatever app claims it.
 *
 * The scheme is all it checks. Pinning the host to the shard we happen to
 * observe would reject the case `getComputerDesktopUrl` goes out of its way to
 * allow: it returns a link from an unexpected host on purpose, warning rather
 * than refusing, because Safari has no CSP to fall foul of and the iOS path
 * still works there. A host check here would turn that into an error toast and
 * leave the fallback unreachable. Web keeps its own frame allowlist, which is
 * where a host belongs.
 */
export const computerDesktopResp = z.object({
  url: z
    .string()
    .url()
    .refine((value) => {
      try {
        return new URL(value).protocol === "https:";
      } catch {
        return false;
      }
    }),
});
export type ComputerDesktopResp = z.infer<typeof computerDesktopResp>;

/**
 * Closing the desktop: the link the client was given, handed back so the server
 * can revoke it. The server never stores the link, so the client is the only
 * place it can come from.
 */
export const computerDesktopCloseReq = z.object({ url: z.string().max(2048) });
export type ComputerDesktopCloseReq = z.infer<typeof computerDesktopCloseReq>;

/**
 * The most a single paste into the desktop may hold. It is typed at about 190
 * characters a second, so this is close to a minute of typing.
 */
export const COMPUTER_PASTE_MAX_CHARS = 10_000;

/** Pasting into the desktop: the text the user's page read from their clipboard. */
export const computerDesktopPasteReq = z.object({
  text: z.string().min(1).max(COMPUTER_PASTE_MAX_CHARS),
});
export type ComputerDesktopPasteReq = z.infer<typeof computerDesktopPasteReq>;

// External file link artifact — a file a connector tool created or updated in
// an external service (e.g. a Word doc in OneDrive), with an "Open" action.
export const externalFileLinkArtifact = heavyArtifact.extend({
  artifactType: z.literal("externalFileLink"),
  sourceSlug: z.string(),
  sourceName: z.string(),
  fileName: z.string(),
  webUrl: z.string(),
  action: z.enum(["created", "updated"]),
});
export type ExternalFileLinkArtifact = z.infer<typeof externalFileLinkArtifact>;

export const schedulingKickoffArtifact = heavyArtifact.extend({
  artifactType: z.literal("schedulingKickoff"),
  from: emailDraftParticipant,
  participants: schedulingKickoffParticipant.array(),
  emailSubject: z.string(),
  messageBody: z.string(),
  subject: z.string(),
  duration: z.number(),
  roughTimeRange: z.object({
    text: z.string(),
    startISO: z.string(),
    endISO: z.string(),
    timezone: z.string().optional(),
  }),
  location: z.string(),
  virtualMeetingType: virtualMeetingType.optional(),
  meetingType: z.string().optional(),
  autoSchedule: z.boolean().optional(),
  /** User-selected availability windows from the calendar picker. */
  userConstraintRanges: z
    .object({
      ranges: z.array(z.object({ startISO: z.string(), endISO: z.string() })),
    })
    .optional(),
  /** When true, skip calendar free/busy check — treat all times as available. */
  skipCalendarConflicts: z.boolean().optional(),
  /** Pre-computed availability ranges from the matched meeting preference (read-only). */
  defaultAvailabilityRanges: z
    .array(z.object({ startISO: z.string(), endISO: z.string() }))
    .optional(),
  /**
   * Proposed times the merged kickoff lists. Authoritative once the
   * kickoff exists; the card renders them as a dedicated block and edits
   * go through the slot picker (not inline in `messageBody`). Times are
   * appended to the email at send time — the `messageBody` never carries them.
   */
  proposedSlots: z
    .array(z.object({ startMsSinceEpoch: z.number(), endMsSinceEpoch: z.number() }))
    .optional(),
  /**
   * Instant Book: the slot currently placed on the picker grid, and the one
   * approval books. Separate from `proposedSlots`, which is the email path's
   * list of times offered to a participant.
   */
  instantBookSlot: z
    .object({
      startMsSinceEpoch: z.number(),
      endMsSinceEpoch: z.number(),
      source: z.enum(["recommended", "user"]).default("recommended"),
      bookOverConflict: z.boolean().optional(),
    })
    .optional(),
  /** No time in the window works for everyone; the picker renders its empty state. */
  instantBookNoMutualTime: z.boolean().optional(),
  /**
   * Whether this kickoff has a working picker — i.e. the draft-time search
   * completed and stored the policy the endpoint needs. False on kickoffs
   * drafted before the picker existed, and on ones whose search fell over;
   * both render the Instant Book banner instead, and book as they always did.
   * Derived from the server-only `instantBookContext`, which never goes on the
   * wire — the client has no business with the availability policy.
   */
  instantBookPickerReady: z.boolean().optional(),
  /**
   * Whether the user hand-edited the email copy, and what the edit replaced.
   * These cross the wire for the ACTOR's benefit, not the client's: the agent
   * reads the hydrated artifact each turn, so this is how it learns the copy is
   * the user's and what it used to say. Without them the lock is enforced
   * server-side but invisible, and any guidance telling the agent to notice the
   * change has nothing to read (BIG-7293).
   */
  userEditedMessageBody: z.boolean().optional(),
  userEditedEmailSubject: z.boolean().optional(),
  generatedMessageBody: z.string().optional(),
  generatedEmailSubject: z.string().optional(),
  role: z.enum(["organizer", "responder"]).default("organizer"),
  /** `approved` is Instant Book's terminal state — booked, no email sent. */
  status: z
    .enum([
      "draft",
      "waitingForUserAction",
      "inProgress",
      "sent",
      "approved",
      "skipped",
      "cancelled",
      "updated",
    ])
    .default("draft"),
});
export type SchedulingKickoffArtifact = z.infer<typeof schedulingKickoffArtifact>;

export const schedulingKickoffActionReq = z.object({
  action: z.enum(["send", "approve", "cancel"]),
  ...tempClientIdField,
});
export type SchedulingKickoffActionReq = z.infer<typeof schedulingKickoffActionReq>;

export const updateSchedulingKickoffReq = z.object({
  messageBody: z.string().optional(),
  emailSubject: z.string().optional(),
  subject: z.string().optional(),
  duration: z.number().optional(),
  roughTimeRange: z
    .object({ text: z.string(), startISO: z.string(), endISO: z.string() })
    .optional(),
  location: z.string().optional(),
  virtualMeetingType: virtualMeetingType.optional().nullable(),
  participants: z
    .array(
      z.object({
        addressId: z.string(),
        contactId: z.string(),
        optional: z.boolean(),
      }),
    )
    .optional(),
  autoSchedule: z.boolean().optional(),
  userConstraintRanges: z
    .object({
      ranges: z.array(z.object({ startISO: z.string(), endISO: z.string() })),
    })
    .optional()
    .nullable(),
  skipCalendarConflicts: z.boolean().optional(),
  // `proposedSlots` is intentionally NOT accepted here. Merged-kickoff slots are
  // edited through the structured artifact-view PATCH route (`applyPatches` in
  // `context/artifactViews/schedulingKickoff.ts`), which validates with the
  // shared `isProposedSlots` guard + length cap. This bulk-update endpoint never
  // persisted the field, so accepting it only invited silent drops.
});
export type UpdateSchedulingKickoffReq = z.infer<typeof updateSchedulingKickoffReq>;

// Booking artifact (durable record of a restaurant/dentist/hotel/… booking)
export const bookingArtifact = heavyArtifact.extend({
  artifactType: z.literal("booking"),
  kind: z.enum(["restaurant"]),
  providerName: z.string(),
  providerUrl: z.string().optional(),
  providerPhone: z.string().optional(),
  channel: z.enum(["online", "phone", "deeplink"]).optional(),
  status: z
    .enum([
      "pending",
      "waiting",
      "waitingForUserAction",
      "confirmed",
      "cancelled",
      "failed",
      "updated",
    ])
    .default("pending"),
  confirmationNumber: z.string().optional(),
  reservationUrl: z.string().optional(),
  when: z.string().optional(),
  summary: z.string().optional(),
  waitingReason: z.string().optional(),
  details: z.record(z.string(), z.unknown()).optional(),
});
export type BookingArtifact = z.infer<typeof bookingArtifact>;

// Phone-call flow artifact — the durable record of a phone-call TASK. Distinct
// from `phoneCallDraft`, which is one call: a task may place several, or none yet.
// Exists so a task waiting on a call, or on three of four replies, is visible to
// the user while it waits.
export const phoneCallFlowArtifact = heavyArtifact.extend({
  artifactType: z.literal("phoneCallFlow"),
  goal: z.string(),
  recipientName: z.string().optional(),
  status: z
    .enum([
      "gathering",
      "ready",
      "calling",
      "waiting",
      "waitingForUserAction",
      "done",
      "failed",
      "updated",
    ])
    .default("gathering"),
  waitingReason: z.string().optional(),
  outcomeSummary: z.string().optional(),
  confirmation: z.string().optional(),
});
export type PhoneCallFlowArtifact = z.infer<typeof phoneCallFlowArtifact>;

export const paymentRequestArtifact = heavyArtifact.extend({
  artifactType: z.literal("paymentRequest"),
  payment: paymentPilotSummarySchema.omit({ actionPath: true }),
  manageUrl: z.string().url().optional(),
  /** Authenticated app endpoint that hands the user the purchase's own browser. */
  handoffUrl: z.string().url().optional(),
});
export type PaymentRequestArtifact = z.infer<typeof paymentRequestArtifact>;
export const shoppingResultsArtifact = heavyArtifact.extend({
  artifactType: z.literal("shoppingResults"),
  shopping: shoppingCardSpec,
  reviewUrl: z.string().url().optional(),
});
export const shoppingOrderArtifact = heavyArtifact.extend({
  artifactType: z.literal("shoppingOrder"),
  shopping: shoppingCardSpec,
  order: catalogOrderSummary,
  reviewUrl: z.string().url().optional(),
});
export type ShoppingResultsArtifact = z.infer<typeof shoppingResultsArtifact>;
export type ShoppingOrderArtifact = z.infer<typeof shoppingOrderArtifact>;

export const getArtifactResp = z.discriminatedUnion("artifactType", [
  emailDraftArtifact,
  invitationArtifact,
  documentArtifact,
  appletArtifact,
  userResponseArtifact,
  phoneCallDraftArtifact,
  callResultsArtifact,
  communicationReceivedArtifact,
  invitationResponseArtifact,
  fileArtifact,
  invitationReceivedArtifact,
  eventChangeArtifact,
  emailChangeArtifact,
  contactChangeArtifact,
  textMessageDraftArtifact,
  directMessageDraftArtifact,
  externalMessageDraftArtifact,
  mcpToolCallArtifact,
  browserActionArtifact,
  paymentRequestArtifact,
  shoppingResultsArtifact,
  shoppingOrderArtifact,
  externalToolCallArtifact,
  gdriveActionArtifact,
  userRequestArtifact,
  routineArtifactResp,
  skillArtifactResp,
  eventArtifactResp,
  reminderArtifact,
  oauthConnectionArtifact,
  pickerArtifact,
  computerSecretArtifact,
  computerDesktopArtifact,
  schedulingKickoffArtifact,
  bookingArtifact,
  phoneCallFlowArtifact,
  externalFileLinkArtifact,
  threadArtifactResp,
]);
export type GetArtifactResp = z.infer<typeof getArtifactResp>;

export const heavyArtifactResponse = z.discriminatedUnion("artifactType", [
  emailDraftArtifact,
  invitationArtifact,
  documentArtifact,
  appletArtifact,
  phoneCallDraftArtifact,
  callResultsArtifact,
  communicationReceivedArtifact,
  invitationResponseArtifact,
  fileArtifact,
  invitationReceivedArtifact,
  eventChangeArtifact,
  emailChangeArtifact,
  contactChangeArtifact,
  textMessageDraftArtifact,
  directMessageDraftArtifact,
  externalMessageDraftArtifact,
  mcpToolCallArtifact,
  browserActionArtifact,
  paymentRequestArtifact,
  shoppingResultsArtifact,
  shoppingOrderArtifact,
  externalToolCallArtifact,
  gdriveActionArtifact,
  eventArtifactResp,
  reminderArtifact,
  routineArtifactResp,
  skillArtifactResp,
  oauthConnectionArtifact,
  pickerArtifact,
  computerSecretArtifact,
  computerDesktopArtifact,
  schedulingKickoffArtifact,
  bookingArtifact,
  phoneCallFlowArtifact,
  externalFileLinkArtifact,
  threadArtifactResp,
]);
export type HeavyArtifactResponse = z.infer<typeof heavyArtifactResponse>;

export const listArtifactsResp = z.object({
  artifacts: z.array(getArtifactResp),
  title: z.string(),
});
export type ListArtifactsResp = z.infer<typeof listArtifactsResp>;

/**
 * The shape both form representations share for answered-ness: the persisted ent
 * (`ent/schema/artifacts.ts`) and the API response. Structural so one predicate
 * serves the server, the web app and native.
 */
type AnswerableForm = { values?: Record<string, string>; supersededAt?: string };

/**
 * Whether a question form is still waiting on the user.
 *
 * Answered two ways, and only one of them writes `values`. A form submitted in
 * the app carries the answers; a form the user answered by replying in prose
 * carries `supersededAt` and no `values`, because the answer never mapped onto
 * fields (BIG-7398). Either way it is no longer awaiting anything.
 *
 * Structurally typed so the persisted ent shape and the API response shape can
 * both use it. Server-side callers that read `ents.data` directly — the
 * supersede in `crud/artifacts.ts` — go through this rather than restating the
 * predicate, so the two cannot drift. The SQL guard on that same write is an
 * unavoidable third encoding; `tests/types/unansweredForm.test.ts` pins the
 * `values: {}` case both have to agree on.
 */
export function formAwaitsAnswer<T extends AnswerableForm>(form: T | undefined): form is T {
  return !!form && form.values === undefined && !form.supersededAt;
}

/**
 * The inverse of {@link formAwaitsAnswer} for a form that exists — "the user has
 * answered this, one way or another".
 *
 * The client-side counterpart: every surface that renders a form asks this to
 * decide whether to disable it and how to label it. Reading `values` directly
 * instead is what left a retired question showing "Waiting for your answer" and
 * still submittable, which is the symptom BIG-7398 was reported as.
 */
export function isFormAnswered(form: AnswerableForm | undefined): boolean {
  return !!form && !formAwaitsAnswer(form);
}

/**
 * How a form was answered, for surfaces that show a status label. A form
 * answered in prose has no `values` to display, so saying only "Answered" would
 * leave the user hunting for answers that were never recorded as fields.
 */
export function formAnsweredLabel(form: AnswerableForm | undefined): string | undefined {
  if (!isFormAnswered(form)) return undefined;
  return form?.values === undefined ? "Answered by reply" : "Answered";
}

/**
 * A `userResponse` form artifact still awaiting answers. Forms are light (no
 * biscuit/status), so they don't pass the heavy-artifact gate — this is how a
 * surface recognizes one to render as a stepped wizard.
 */
export function isUnansweredForm(a: GetArtifactResp): a is UserResponseArtifact {
  return a.artifactType === "userResponse" && formAwaitsAnswer(a.form);
}

/**
 * Whether an artifact is awaiting the user on the current turn: a heavy artifact
 * paused for approval (with a rendered `biscuit`), or an unanswered userResponse
 * form. The single gate behind every "waiting for user approval" surface — the
 * thread-status reclassification at FINAL, the approval email's look-back
 * (`selectActionableArtifacts`), and the web omni-overlay (`selectOmniArtifacts`)
 * — so they never disagree on what counts as pending.
 */
export function isActionableArtifact(
  a: GetArtifactResp,
): a is HeavyArtifactResponse | UserResponseArtifact {
  if (a.isHeavy) return a.status === "waitingForUserAction" && !!a.biscuit;
  return isUnansweredForm(a);
}

/**
 * Artifact types whose card stays editable once saved.
 *
 * Membership means the artifact *is* the durable object: no separate ent
 * behind it, no page elsewhere that edits it, and no copy that has left the
 * system which a late edit could contradict. A document is one. Every other
 * type is a proposal, and a proposal stops being editable the moment it is
 * acted on — a sent email edited after the fact would misdescribe what went
 * out.
 */
export const EDITABLE_AFTER_SAVE_ARTIFACT_TYPES: ReadonlySet<string> = new Set(["document"]);

/**
 * Whether an artifact at `status` may still be edited after its save.
 *
 * One rule for the server's patch gate, the server-built view and the
 * clients' read-only flags, so they cannot drift: a card that offers an edit
 * the server refuses, or hides one it would accept, is the failure this
 * exists to prevent. Written as an explicit allow — a status not named here
 * is closed, including `undefined` on artifacts from before their type had a
 * lifecycle.
 */
export function isEditableAfterSave(artifactType: string, status: string | undefined): boolean {
  return status === "approved" && EDITABLE_AFTER_SAVE_ARTIFACT_TYPES.has(artifactType);
}

export const sendDraftEmailReq = z.object({
  action: z.enum(["send", "cancel"]),
  ...tempClientIdField,
});
export type SendDraftEmailReq = z.infer<typeof sendDraftEmailReq>;

export const sendInvitationReq = z.object({
  action: z.enum(["send", "cancel"]),
  ...tempClientIdField,
});
export type SendInvitationReq = z.infer<typeof sendInvitationReq>;

export const sendRsvpResponseReq = z.object({
  response: z.enum(["yes", "no", "maybe"]),
});
export type SendRsvpResponseReq = z.infer<typeof sendRsvpResponseReq>;

export const makePhoneCallReq = z.object({
  /** `makeAll` places this call AND grants the thread permission to place later calls
   * without a card (BIG-6833) — see `autoPlacePhoneCalls` on `ActionPermissions`. */
  action: z.enum(["make", "makeAll", "cancel"]),
  ...tempClientIdField,
});
export type MakePhoneCallReq = z.infer<typeof makePhoneCallReq>;

export const sendTextMessageReq = z.object({
  /** `sendAll` sends this text AND grants the thread permission to send later texts
   * without a card — see `autoSendTextMessages` on `ActionPermissions`. */
  action: z.enum(["send", "sendAll", "cancel"]),
  ...tempClientIdField,
});
export type SendTextMessageReq = z.infer<typeof sendTextMessageReq>;

export const sendDirectMessageReq = z.object({
  action: z.enum(["send", "cancel"]),
  ...tempClientIdField,
});
export type SendDirectMessageReq = z.infer<typeof sendDirectMessageReq>;

export const updateDirectMessageDraftReq = z.object({
  body: z.string(),
});
export type UpdateDirectMessageDraftReq = z.infer<typeof updateDirectMessageDraftReq>;

export const executeBriefReq = z.object({
  action: z.enum(["execute", "cancel"]),
});
export type ExecuteBriefReq = z.infer<typeof executeBriefReq>;

export const schedulingSuggestionActionReq = z.object({
  action: z.enum(["approve", "decline", "cancel", "accept", "counterPropose"]),
  ...tempClientIdField,
});
export type SchedulingSuggestionActionReq = z.infer<typeof schedulingSuggestionActionReq>;

export const eventChangeActionReq = z.object({
  action: z.enum(["apply", "cancel"]),
  ...tempClientIdField,
});
export type EventChangeActionReq = z.infer<typeof eventChangeActionReq>;

// A routine is installed when it is saved, so the card has nothing to approve
// or discard — "run" is the only action it offers.
export const routineArtifactActionReq = z.object({
  action: z.enum(["run"]),
});
export type RoutineArtifactActionReq = z.infer<typeof routineArtifactActionReq>;

/**
 * Approve/cancel decision on a confirm-gated artifact whose execution lives in
 * an agent module (Slack/Teams sends, MCP + OpenAPI tool calls, Drive
 * mutations). Those modules resolve approval from the user's next reply, so
 * the handler posts that reply on the user's behalf rather than executing the
 * action itself.
 */
export const pendingApprovalActionReq = z.object({
  action: z.enum(["approve", "cancel"]),
  ...tempClientIdField,
});
export type PendingApprovalActionReq = z.infer<typeof pendingApprovalActionReq>;

export const updateRoutineArtifactReq = z.object({
  name: z.string().optional(),
  // Capped here as well as on the ent schema: ent writes merge `data` without
  // re-parsing it against the per-type schema, so the boundary is the only
  // place an over-long description is actually refused.
  description: z.string().max(ROUTINE_DESCRIPTION_MAX_LENGTH).optional(),
  prompt: z.string().optional(),
  triggerConfig: triggerConfigSchema.optional(),
  permissions: z
    .object({
      autoSendCommunication: z.boolean(),
      autoArchive: z.boolean(),
      defaultFrom: z.enum(["user", "assistant"]),
    })
    .partial()
    .optional(),
  enabled: z.boolean().optional(),
});
export type UpdateRoutineArtifactReq = z.infer<typeof updateRoutineArtifactReq>;

export const submitFormReq = z.object({
  values: z.record(z.string(), z.string()),
  ...tempClientIdField,
});
export type SubmitFormReq = z.infer<typeof submitFormReq>;

export type SubmitFormResp = {
  success: boolean;
  error?: string;
  /** Per-field validation errors keyed by `field.id`. Set when `success` is false and the failure was a recoverable input error (vs. a server fault). */
  fieldErrors?: Record<string, string>;
};

// --- Applet storage (localStorage shim backing store) ------------------------
// The applet iframe's `localStorage` shim mirrors its writes up to the parent,
// which posts the whole blob here. Only the blob travels — the applet identity
// is derived server-side from the route's artifactId, never trusted from the
// payload. Quotas are enforced server-side (see crud/appletStorage).
export const appletStorageWriteReq = z.object({
  blob: z.record(z.string(), z.string()),
});
export type AppletStorageWriteReq = z.infer<typeof appletStorageWriteReq>;

export type AppletStorageWriteResp = {
  success: boolean;
  error?: string;
  /** Which quota tripped, when the write was rejected for size. */
  quotaKind?: "keyCount" | "valueSize" | "totalSize";
};

// --- Batch commit (omni-artifact staged review queue, BIG-5269) ---------------
// The client reviews several pending artifacts and stages a decision per
// artifact, then commits them all at once so the agent runs a single time.
// An "action" decision names the artifact + the action id (re-derived
// server-side via buildArtifactActions — never trust a client handlerPath), with
// an optional accompanying note. A "note" decision is a free-text instruction
// with no action taken. A "form" decision carries the user's answers to a
// userResponse form artifact's fields (submitted via submitArtifactForm). Skipped
// artifacts are simply omitted from the request.

export const artifactDecision = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("action"),
    artifactId: z.string(),
    actionId: z.string(),
    note: z.string().optional(),
  }),
  z.object({
    kind: z.literal("note"),
    artifactId: z.string(),
    note: z.string().min(1),
  }),
  z.object({
    kind: z.literal("form"),
    artifactId: z.string(),
    /** Answers keyed by `form.fields[].id` — same shape as `submitFormReq`. */
    values: z.record(z.string(), z.string()),
  }),
]);
export type ArtifactDecision = z.infer<typeof artifactDecision>;

export const artifactDecisionsReq = z.object({
  decisions: z.array(artifactDecision).min(1),
  /**
   * Files the user attached while answering (BIG-9261), already uploaded. They
   * ride on the batch's one committed message, the same way a composer send
   * carries them, so the agent reads the answers and the files in one run.
   * Optional, so a client that predates it keeps parsing.
   */
  fileIds: z.array(z.string()).optional(),
  // Here the client also writes a local placeholder under the id (it composes
  // the commit text itself, so it can), and the mapping resolves that
  // placeholder rather than only marking the arriving message.
  ...tempClientIdField,
});
export type ArtifactDecisionsReq = z.infer<typeof artifactDecisionsReq>;

export const artifactDecisionResult = z.object({
  artifactId: z.string(),
  status: z.enum(["applied", "noted", "submitted", "failed"]),
  error: z.string().optional(),
});
export type ArtifactDecisionResult = z.infer<typeof artifactDecisionResult>;

export const artifactDecisionsResp = z.object({
  success: z.boolean(),
  results: z.array(artifactDecisionResult),
  workflowEnqueued: z.boolean(),
  /** Thread event id of the committed `userRequest`, so the client can resolve
   *  the placeholder it wrote under `tempClientId` without waiting on the
   *  `idToClientId` socket event. Absent when the batch produces no committed
   *  content, such as when every decision fails or is an idempotent no-op. */
  eventId: z.number().optional(),
});
export type ArtifactDecisionsResp = z.infer<typeof artifactDecisionsResp>;
